import { MLHelper } from '@bimser/core';
import VectorMap, {
	Annotation,
	ControlBar,
	Export,
	Label,
	Layer,
	Legend,
	Source,
	Title
} from 'devextreme-react/vector-map';
import CustomStore from 'devextreme/data/custom_store';
import React from 'react';
import { IVectorMapProps } from '../entities/IVectorMapProps';

const mapLoader = async (name: string) => {
	if (name) {
		return (await import(`devextreme-dist/js/vectormap-data/${name.toLowerCase()}.js`))[
			name.toLowerCase()
		];
	}
};

export default (props: IVectorMapProps) => {
	const dataSourceRefs = React.useRef(
		props.layer.map(layer => ({
			name: layer.name,
			store: new CustomStore({ load: () => mapLoader(layer.dataSource) })
		}))
	);

	const customizeLayer = elements => {
		const selectedValues: any = {};
		let paletTitle = '';
		props.layer.forEach(item => {
			paletTitle = item.colorGroupingField || item.sizeGroupingField;
			if (item.data?.length) {
				item.data?.map(i =>
					Object.assign(selectedValues, { [i.label]: { value: i.value, color: i.color } })
				);
			}

			elements.forEach((element, index) => {
				const items = selectedValues[element.attribute('name')];
				if (items) {
					element.applySettings({
						color: items.color,
						hoveredColor: '#e0e000',
						selectedColor: '#008f00',
						paletteIndex: index % 7
					});
					element.attribute(paletTitle, items.value);
				}
			});
		});
	};

	function customizeText(arg) {
		if (arg.index === 0) {
			return '< 0.5%';
		} else if (arg.index === 5) {
			return '> 3%';
		}
		return `${arg.start}% to ${arg.end}%`;
	}

	function layerGroups(item) {
		if (item?.length) {
			return item.split(',').map(i => parseFloat(i));
		}
	}

	function coordinates(item) {
		if (item) {
			return [item.lat, item.lng];
		}
	}

	function annotationData(item) {
		let data = {};
		if (item?.length) {
			data = item.reduce((acc: any, cur: any) => {
				acc[cur.label] = cur.value;
				return acc;
			}, {});
		}
		return data;
	}

	function annotationTemplate(annotation: any) {
		const { data } = annotation;

		return (
			<svg className="annotation">
				<text
					x="0"
					y="60"
				>
					{Object.keys(data).map(key => (
						<React.Fragment key={key}>
							<tspan
								className="caption"
								dy="14"
								x="0"
							>
								{key}:
							</tspan>
							<tspan dx="5">{data[key]}</tspan>
						</React.Fragment>
					))}
				</text>
			</svg>
		);
	}

	return (
		<VectorMap
			id={props.id}
			height={props.height}
			bounds={props.bounds}
		>
			<ControlBar enabled={props.controlBar} />

			<Title
				text={MLHelper.getMLText(props.titleMap?.text)}
				subtitle={MLHelper.getMLText((props.titleMap?.subtitle as any)?.text)}
				font={props.titleMap?.font}
				horizontalAlignment={props.titleMap?.horizontalAlignment}
				verticalAlignment={props.titleMap?.verticalAlignment}
			/>

			{props.layer.map((item, index) => {
				const { store } = dataSourceRefs.current.find(({ name }) => name === item.name);

				return (
					<Layer
						key={index}
						dataSource={store}
						borderColor={item.borderColor}
						name={item.name}
						colorGroups={layerGroups(item.colorGroups)}
						colorGroupingField={item.colorGroupingField}
						sizeGroups={layerGroups(item.sizeGroups)}
						sizeGroupingField={item.sizeGroupingField}
						customize={customizeLayer}
						palette={item.palette}
						hoverEnabled={item.hoverEnabled}
						hoveredColor={item.hoveredColor}
					>
						<Label
							enabled
							dataField="name"
						/>
					</Layer>
				);
			})}

			{props.legend.map((item, index) => (
				<Legend
					key={index}
					title={MLHelper.getMLText(item.title)}
					font={item.font}
					markerShape={item.markerShape}
					horizontalAlignment={item.horizontalAlignment}
					verticalAlignment={item.verticalAlignment}
					customizeText={customizeText}
					visible={item.visible}
				>
					<Source
						layer={(item.source as any).layer}
						grouping={(item.source as any).grouping}
					/>
				</Legend>
			))}

			{props.annotation.map((item, index) => (
				<Annotation
					key={index}
					coordinates={coordinates(item.coordinate)}
					data={annotationData(item.data)}
					offsetX={item.offsetX}
					offsetY={item.offsetY}
					arrowWidth={item.arrowWidth}
					arrowLength={item.arrowLength}
					color={item.color}
					type="custom"
					width={item.width}
					height={item.height}
					render={annotationTemplate}
				/>
			))}

			<Export
				fileName={props.exportMap.fileName}
				formats={props.exportMap.formats}
				backgroundColor={props.exportMap.backgroundColor}
				printingEnabled={props.exportMap.printingEnabled}
				enabled={props.exportMap.enabled}
			/>
		</VectorMap>
	);
};
